import { type SpatialEntityFields, type Template } from '@ekkogmbh/apisdk';
import { Box, Collapse, List, ListItem, ListItemText } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import React, { useCallback, useMemo, useState } from 'react';
import { TemplatePreview } from './TemplatePreview';

interface Props {
  templates: Template[];
  fields: SpatialEntityFields;
  coordinate: string;
  coordinateFieldName: string;
  labelIdFieldName: string;
}

export const TemplatePreviewList = ({
  templates,
  fields,
  coordinate,
  coordinateFieldName,
  labelIdFieldName,
}: Props): React.JSX.Element => {
  const [openIndices, setOpenIndices] = useState<number[]>([]);

  const onOpenPreview = useCallback(
    (index: number) => {
      openIndices.push(index);
      setOpenIndices([...new Set(openIndices)]);
    },
    [openIndices],
  );

  const onClosePreview = useCallback(
    (index: number) => {
      const idx = openIndices.indexOf(index);
      if (idx >= 0) {
        openIndices.splice(idx, 1);
        setOpenIndices([...openIndices]);
      }
    },
    [openIndices],
  );

  const renderFields = useMemo(
    (): SpatialEntityFields => ({
      [coordinateFieldName]: coordinate,
      [labelIdFieldName]: 'labelId',
      ...fields,
    }),
    [coordinate, coordinateFieldName, labelIdFieldName, fields],
  );

  const renderListItem = useCallback(
    (template: Template, index: number) => {
      const isOpen = openIndices.includes(index);
      return (
        <Box key={index} component={'div'}>
          <ListItem
            onClick={() => {
              isOpen ? onClosePreview(index) : onOpenPreview(index);
            }}
          >
            <ListItemText primary={template.name} />
            {isOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={isOpen} timeout={'auto'} unmountOnExit>
            <TemplatePreview template={template} fields={renderFields} />
          </Collapse>
        </Box>
      );
    },
    [renderFields, openIndices],
  );

  return <List>{templates.map((template, i) => renderListItem(template, i))}</List>;
};
